// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
// @greenlight-spec: 11-observability.md#metric-history

import type { HealthIncident, HealthSeries, HealthSeriesStep } from '@greenlight/shared';

export type HistoryRange = '1h' | '24h' | '7d' | '30d';

/** Each range's step keeps the window inside the series read's bucket cap. */
export const HISTORY_RANGES: Record<HistoryRange, { windowMs: number; step: HealthSeriesStep }> = {
  '1h': { windowMs: 3_600_000, step: '1m' },
  '24h': { windowMs: 86_400_000, step: '5m' },
  '7d': { windowMs: 7 * 86_400_000, step: '1h' },
  '30d': { windowMs: 30 * 86_400_000, step: '2h' },
};
export const HISTORY_RANGE_ORDER: readonly HistoryRange[] = ['1h', '24h', '7d', '30d'];

export interface HistoryWindow {
  from: string;
  to: string;
  step: HealthSeriesStep;
}

/** Aligned to the step, so a refetch inside one step asks for the same buckets. */
export function historyWindow(range: HistoryRange, nowMs: number): HistoryWindow {
  const { windowMs, step } = HISTORY_RANGES[range];
  const stepMs = stepMsOf(step);
  const to = Math.ceil(nowMs / stepMs) * stepMs;
  return { from: new Date(to - windowMs).toISOString(), to: new Date(to).toISOString(), step };
}

export function stepMsOf(step: HealthSeriesStep): number {
  return { '1m': 60_000, '5m': 300_000, '1h': 3_600_000, '2h': 7_200_000 }[step];
}

/**
 * One bucket as a chart draws it. `avg` and `peak` are null together when
 * nothing was observed, which the chart draws as a gap, never as zero.
 */
export interface HistoryPoint {
  t: number;
  avg: number | null;
  peak: number | null;
  /** Which scope the value came from, when the chart shows the worst of several. */
  scope?: string | null;
  /** Observed, but with no limit to express it against; not a gap. */
  noLimit?: boolean;
}

/** Bucket start times for a series response, which every scope shares. */
function bucketTimes(series: HealthSeries): number[] {
  const first = series.scopes[0];
  if (first) return first.buckets.map((b) => Date.parse(b.t));
  const stepMs = stepMsOf(series.step);
  const from = Date.parse(series.from);
  const count = Math.ceil((Date.parse(series.to) - from) / stepMs);
  return Array.from({ length: count }, (_, i) => from + i * stepMs);
}

const percentOf = (value: number | null, limit: number | null): number | null =>
  value === null || limit === null || limit <= 0 ? null : (value / limit) * 100;

/**
 * The scope nearest its own limit in each bucket, as a percent of that limit.
 * Replicas are never averaged together: the mean of a saturated pod and an
 * idle one is a number no container is experiencing.
 */
export function worstScopePercent(series: HealthSeries): HistoryPoint[] {
  return bucketTimes(series).map((t, i) => {
    let best: HistoryPoint = { t, avg: null, peak: null };
    for (const scope of series.scopes) {
      const bucket = scope.buckets[i];
      if (!bucket || bucket.observations === 0) continue;
      const peak = percentOf(bucket.peak, bucket.limit);
      if (peak === null) {
        if (best.peak === null) best = { t, avg: null, peak: null, noLimit: true };
        continue;
      }
      if (best.peak !== null && peak <= best.peak) continue;
      best = { t, avg: percentOf(bucket.avg, bucket.limit), peak, scope: scope.scope };
    }
    return best;
  });
}

/** One scope as a percent of its own limit (a database's connections of its maximum). */
export function scopePercent(series: HealthSeries, scope: string): HistoryPoint[] {
  const found = series.scopes.find((s) => s.scope === scope);
  return bucketTimes(series).map((t, i) => {
    const bucket = found?.buckets[i];
    if (!bucket || bucket.observations === 0) return { t, avg: null, peak: null };
    const peak = percentOf(bucket.peak, bucket.limit);
    if (peak === null) return { t, avg: null, peak: null, noLimit: true };
    return { t, avg: percentOf(bucket.avg, bucket.limit), peak };
  });
}

/** The tighter of two percent series, bucket by bucket (cluster CPU and memory). */
export function tighterOf(a: HistoryPoint[], b: HistoryPoint[]): HistoryPoint[] {
  return a.map((pa, i) => {
    const pb = b[i];
    if (!pb || pb.peak === null) return pa;
    if (pa.peak === null) return pb;
    return pb.peak > pa.peak ? pb : pa;
  });
}

/** A counter summed across scopes; a bucket nothing observed stays a gap. */
export function sumAcrossScopes(series: HealthSeries): HistoryPoint[] {
  return bucketTimes(series).map((t, i) => {
    let total: number | null = null;
    for (const scope of series.scopes) {
      const bucket = scope.buckets[i];
      if (!bucket || bucket.observations === 0 || bucket.sum === null) continue;
      total = (total ?? 0) + bucket.sum;
    }
    return { t, avg: total, peak: total };
  });
}

/** One scope's own readings, average and peak (the oldest waiting job's age). */
export function scopeValues(series: HealthSeries, scope: string | null): HistoryPoint[] {
  const found = series.scopes.find((s) => s.scope === scope);
  return bucketTimes(series).map((t, i) => {
    const bucket = found?.buckets[i];
    if (!bucket || bucket.observations === 0) return { t, avg: null, peak: null };
    return { t, avg: bucket.avg, peak: bucket.peak };
  });
}

/** The worst per-minute value in each bucket for one scope (a route family's p95). */
export function scopePeak(series: HealthSeries, scope: string | null): HistoryPoint[] {
  const found = series.scopes.find((s) => s.scope === scope);
  return bucketTimes(series).map((t, i) => {
    const bucket = found?.buckets[i];
    if (!bucket || bucket.observations === 0) return { t, avg: null, peak: null };
    return { t, avg: null, peak: bucket.peak };
  });
}

/** The lowest reading in each bucket for one scope (ready replicas' floor). */
export function scopeFloor(
  series: HealthSeries,
  scope: string,
): {
  points: HistoryPoint[];
  limit: number | null;
} {
  const found = series.scopes.find((s) => s.scope === scope);
  let limit: number | null = null;
  const points = bucketTimes(series).map((t, i) => {
    const bucket = found?.buckets[i];
    if (!bucket || bucket.observations === 0) return { t, avg: null, peak: null };
    limit = bucket.limit ?? limit;
    return { t, avg: bucket.min, peak: bucket.min };
  });
  return { points, limit };
}

/**
 * Server errors over requests, summed across route families. A bucket with no
 * requests has no rate: it is unavailable, not a zero error rate.
 */
export function errorRate(requests: HealthSeries, errors: HealthSeries): HistoryPoint[] {
  const req = sumAcrossScopes(requests);
  const err = sumAcrossScopes(errors);
  return req.map((r, i) => {
    const e = err[i]?.peak ?? null;
    if (r.peak === null || r.peak <= 0 || e === null) return { t: r.t, avg: null, peak: null };
    const rate = (e / r.peak) * 100;
    return { t: r.t, avg: rate, peak: rate };
  });
}

export interface HistoryMarker {
  t: number;
  kind: 'incident' | 'release';
  label: string;
}

/**
 * Where the running release changed inside the window. Compared with the last
 * interval that was observed, so the start of history, a collection gap, and a
 * version already running when the window opens are not changes, while a
 * rollback to an earlier version is.
 */
export function releaseMarkers(series: HealthSeries): HistoryMarker[] {
  const times = bucketTimes(series);
  const markers: HistoryMarker[] = [];
  let previous: Set<string> | null = null;
  times.forEach((t, i) => {
    const running = new Set(
      series.scopes.flatMap((scope) =>
        scope.scope && (scope.buckets[i]?.observations ?? 0) > 0 ? [scope.scope] : [],
      ),
    );
    if (running.size === 0) return;
    if (previous) {
      for (const version of running) {
        if (!previous.has(version))
          markers.push({ t, kind: 'release', label: `Release ${version}` });
      }
    }
    previous = running;
  });
  return markers;
}

/**
 * Why a percent-of-limit chart has nothing to draw when readings exist: the
 * replicas ran with no limit, so there is nothing to divide by.
 */
export function noLimitMessage(series: HealthSeries, points: HistoryPoint[]): string | undefined {
  const observed = series.scopes.some((scope) => scope.buckets.some((b) => b.observations > 0));
  const drawn = points.some((p) => p.peak !== null || p.avg !== null);
  return observed && !drawn
    ? 'Readings exist, but no limit was set to compare them against.'
    : undefined;
}

const INCIDENT_WORDS: Record<HealthIncident['kind'], string> = {
  container_terminated: 'Container terminated',
  memory_pressure: 'Memory pressure',
  readiness: 'No ready replicas',
  queue_trouble: 'Background work stalled',
  dependency_degraded: 'Cloud dependency degraded',
};

export function incidentLabel(incident: Pick<HealthIncident, 'kind' | 'tier' | 'reason'>): string {
  const what = INCIDENT_WORDS[incident.kind];
  return incident.tier ? `${what} (${incident.tier})` : what;
}

export function incidentMarkers(incidents: HealthIncident[]): HistoryMarker[] {
  return incidents.map((incident) => ({
    t: Date.parse(incident.occurred_at),
    kind: 'incident',
    label: incidentLabel(incident),
  }));
}

/** Which bucket a moment falls in, or -1 outside the window. */
export function bucketIndexOf(points: HistoryPoint[], t: number, stepMs: number): number {
  const first = points[0];
  if (!first) return -1;
  const index = Math.floor((t - first.t) / stepMs);
  return index >= 0 && index < points.length ? index : -1;
}

/**
 * Whether some observed readings had no limit, so a percent-of-limit chart left
 * them out. The chart says so, rather than shading them as unobserved without a word.
 */
export function someWithoutLimit(series: HealthSeries): boolean {
  return series.scopes.some((scope) =>
    scope.buckets.some((b) => b.observations > 0 && (b.limit === null || b.limit <= 0)),
  );
}
